import {Input, Textarea, Button} from '../../ui/Mobile';
import { useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { useBlocker, useLocation, useParams } from "react-router-dom";
import { Link, useNavigate, useSearchParams } from "../../app/navigation";
import { useService } from '../../app/context';
import { EditorController } from '../../application/editor-controller';
import { hasContent } from '../../domain/capture/model';
import { errorMessage } from '../../domain/shared/errors';
import { prepareImage } from '../../infrastructure/attachments/prepare';
import { exportText } from '../../infrastructure/platform/files';
import { AttachmentImage } from '../../ui/AttachmentImage';
import { Icon } from '../../ui/Icons';

export function EditorRoute() {
  const service = useService(); const { id } = useParams();
  const [params] = useSearchParams(); const draftId = params.get('draft');
  const [state, setState] = useState<{ controller?: EditorController; error?: string }>({});
  useEffect(() => {
    let cancelled = false; let instance: EditorController | undefined;
    void (async () => {
      try {
        if (draftId) {
          const draft = await service.db.drafts.get(draftId);
          if (!draft || draft.deletedAt || draft.captureId !== id) throw new Error('这份草稿不存在或已提交。请返回收件箱查看。');
          instance = new EditorController(service, draft.baseRevision, draft);
        } else if (id) { const capture=await service.db.captures.get(id); if(!capture || capture.deletedAt) throw new Error('记录不存在或已在回收站。'); instance=new EditorController(service,capture.revision,undefined,capture.id,capture.body,capture.attachmentIds); }
        else instance = new EditorController(service, 0);
        if (!cancelled) setState({ controller: instance });
      } catch (error) { if (!cancelled) setState({ error: error instanceof Error ? error.message : '无法读取草稿。' }); }
    })();
    return () => { cancelled = true; instance?.dispose(); };
  }, [service, id, draftId]);
  if (state.error) return <main className="page"><Link className="back-link" to="/inbox"><Icon name="arrow"/>返回收件箱</Link><h1>草稿未打开</h1><p role="alert">{state.error}</p></main>;
  return state.controller ? <CaptureEditor key={`${state.controller.getSnapshot().draftId}:${params.get('view') || 'full'}`} controller={state.controller} sheet={params.get('view') === 'sheet'}/> : <main className="page">正在打开编辑器…</main>;
}

function CaptureEditor({ controller, sheet }: { controller: EditorController; sheet: boolean }) {
  const service = useService(); const navigate = useNavigate(); const location = useLocation();
  const value = useSyncExternalStore(controller.subscribe, controller.getSnapshot);
  const [processing, setProcessing] = useState(false); const [submitting, setSubmitting] = useState(false);
  const [attachmentError, setAttachmentError] = useState(''); const [actionError, setActionError] = useState('');
  const [discard, setDiscard] = useState(false);
  const allowLeave = useRef(false); const publishOperation = useRef<{ signature: string; id: string } | null>(null);
  const dialog = useRef<HTMLDialogElement>(null); const textarea = useRef<HTMLTextAreaElement>(null); const fileInput = useRef<HTMLInputElement>(null);
  const blocker = useBlocker(() => !allowLeave.current && (controller.isDirty || processing || submitting));
  const from = typeof location.state?.from === 'string' && location.state.from.startsWith('/') && !location.state.from.startsWith('//') ? location.state.from : '/inbox';

  useEffect(() => { if (sheet && dialog.current && !dialog.current.open) dialog.current.showModal(); textarea.current?.focus(); }, [sheet]);
  useEffect(() => {
    const warn = (e: BeforeUnloadEvent) => { if (controller.isDirty || processing || submitting) { e.preventDefault(); e.returnValue = ''; } };
    const checkpoint = () => { if (document.visibilityState === 'hidden') void controller.flush(); };
    window.addEventListener('beforeunload', warn); document.addEventListener('visibilitychange', checkpoint);
    return () => { window.removeEventListener('beforeunload', warn); document.removeEventListener('visibilitychange', checkpoint); };
  }, [controller, processing, submitting]);
  useEffect(() => {
    if (blocker.state !== 'blocked' || processing || submitting) return;
    let valid = true;
    void controller.flush().then((ok) => { if (valid && ok) blocker.proceed(); });
    return () => { valid = false; };
  }, [blocker, controller, processing, submitting]);

  async function close() {
    if (processing || submitting) return;
    if (!(await controller.flush())) return;
    allowLeave.current = true; navigate(from);
  }
  async function expand() {
    controller.ensureDraft();
    if (!(await controller.flush())) return;
    const current = controller.getSnapshot();
    allowLeave.current = true;
    navigate(`/captures/${current.captureId}/edit?draft=${current.draftId}`, { replace: true, state: { from } });
  }
  async function addFiles(files: FileList | null) {
    if (!files?.length) return;
    setProcessing(true); setAttachmentError('');
    const errors: string[] = [];
    for (const file of Array.from(files)) {
      try {
        if (controller.getSnapshot().attachmentIds.length >= 30) throw new Error('当前每条记录最多添加 30 张图片。');
        controller.addAttachment(await prepareImage(file));
      } catch (error) { errors.push(`${file.name}：${error instanceof Error ? error.message : '图片读取失败'}`); }
    }
    setAttachmentError(errors.join('\n')); setProcessing(false);
    if (fileInput.current) fileInput.current.value = '';
    await controller.flush();
  }
  async function submit(asCopy = false) {
    if (submitting || processing) return;
    setSubmitting(true); setActionError('');
    try {
      controller.ensureDraft();
      if (!(await controller.flush())) return;
      const current = controller.getSnapshot();
      const signature = `${current.draftId}:${current.revision}:${asCopy}`;
      if (publishOperation.current?.signature !== signature) publishOperation.current = { signature, id: crypto.randomUUID() };
      const result = await service.publish(current.draftId, current.revision, publishOperation.current.id, asCopy);
      if (result.kind === 'conflict') { setActionError('另一处已有新版本。双方内容均已保留，请将这份草稿另存为新记录。'); return; }
      allowLeave.current = true; navigate(from.startsWith('/captures/')?`/captures/${result.capture.id}`:'/inbox', { replace: true, state: { saved: result.capture.id } });
    } catch (error) { setActionError(errorMessage(error)); }
    finally { setSubmitting(false); }
  }
  async function copy() {
    try { await navigator.clipboard.writeText(value.body); setActionError('文本已复制。图片需另行保存。'); }
    catch { textarea.current?.focus(); textarea.current?.select(); setActionError('已选中文本，请使用系统复制，或导出文本文件。'); }
  }
  async function discardDraft() {
    if (submitting || processing) return;
    setSubmitting(true);
    try {
      // Drain writes before deleting; failed unsaved text is discarded only by this explicit confirmation.
      await controller.flush();
      const current = controller.getSnapshot();
      if (current.revision) await service.discardDraft(current.draftId, current.revision, crypto.randomUUID());
      allowLeave.current = true; navigate(from, { replace: true });
    } catch (error) { setActionError(errorMessage(error)); }
    finally { setSubmitting(false); }
  }

  const content = <div className={`editor ${sheet ? 'sheet-editor' : 'full-editor'}`}>
    <div className="sheet-handle" aria-hidden="true"/>
    <header className="editor-header"><div><p className="eyebrow">不必先分类</p><h1 id="editor-title">记一下</h1></div><div className="editor-header-actions">
      {sheet && <Button className="icon-button" disabled={processing || submitting} aria-label="展开编辑" onClick={() => void expand()}><Icon name="expand"/></Button>}
      <Button className="icon-button" disabled={processing || submitting} aria-label="保存草稿并关闭" onClick={() => void close()}><Icon name="close"/></Button>
    </div></header>
    <div className={`save-status ${value.status}`} role="status" aria-live="polite"><span className="status-dot"/>{value.message}</div>
    <div className="editor-scroll">
      <label className="sr-only" htmlFor="capture-body">记录内容</label>
      <Textarea id="capture-body" ref={textarea} value={value.body} maxLength={200000} disabled={submitting} placeholder="想到什么，就写在这里…" onChange={(e) => controller.setBody(e.target.value)} spellCheck={false}/>
      <div className="attachment-grid">{value.attachmentIds.map((id) => <AttachmentImage key={id} id={id} prepared={controller.getPrepared(id)} onRemove={submitting ? undefined : () => controller.removeAttachment(id)}/>)}</div>
      <Input ref={fileInput} className="sr-only" type="file" accept="image/jpeg,image/png,image/gif,image/webp,image/avif,image/heic,image/heif" multiple aria-label="选择图片" onChange={(e) => void addFiles(e.target.files)}/>
      {attachmentError && <div className="notice error" role="alert">{attachmentError}<p>可继续保存文字；未成功添加的图片不会显示为已保存。</p></div>}
      {actionError && <div className="notice" role="status">{actionError}</div>}
      {(value.status === 'error' || actionError) && <div className="recovery-actions"><Button className="button secondary" onClick={() => void controller.flush()}>重试保存</Button><Button className="button secondary" onClick={() => void copy()}>复制文本</Button><Button className="button secondary" onClick={() => exportText(value.body)}>导出文本</Button></div>}
      {(value.status === 'conflict' || actionError.includes('另一处')) && <Button className="button secondary" disabled={submitting} onClick={() => void submit(true)}>将这份草稿另存为新记录</Button>}
      {blocker.state === 'blocked' && <div className="notice error" role="alert"><p>{processing ? '图片仍在处理中，请稍候。' : '尚有未保存的内容。保存成功后才会离开。'}</p><div className="recovery-actions"><Button className="button secondary" onClick={() => blocker.reset()}>继续编辑</Button><Button className="button secondary" onClick={() => exportText(value.body)}>导出文本</Button><Button className="button danger" onClick={() => { if (window.confirm('未成功保存的输入将丢失。确定离开？')) blocker.proceed(); }}>放弃未保存并离开</Button></div></div>}
      {discard && <div className="notice"><p>将这份草稿移入回收站？已存下的其他记录不会受影响。未成功落盘的输入不能由回收站恢复。</p><div className="recovery-actions"><Button className="button secondary" onClick={() => setDiscard(false)}>继续保留</Button><Button className="button danger" disabled={submitting || processing} onClick={() => void discardDraft()}>确认丢弃草稿</Button></div></div>}
    </div>
    <footer className="editor-footer"><div className="editor-tools"><Button className="button quiet" disabled={processing || submitting} onClick={() => fileInput.current?.click()}><Icon name="image"/>{processing ? '正在处理图片…' : '添加图片'}</Button><Button className="text-button muted" disabled={submitting || processing} onClick={() => setDiscard(true)}>丢弃草稿</Button></div>
      <Button className="button primary" disabled={submitting || processing || !hasContent(value.body, value.attachmentIds)} onClick={() => void submit()}>{submitting ? '正在存下…' : '存到收件箱'}</Button>
    </footer>
  </div>;
  return sheet ? <dialog ref={dialog} className="capture-dialog" aria-labelledby="editor-title" onCancel={(e) => { e.preventDefault(); void close(); }} onClick={(e) => { if (e.target === dialog.current) void close(); }}>{content}</dialog> : <main className="editor-page" id="main">{content}</main>;
}
